import {
	Button,
	Popover,
	Menu,
	MenuDivider,
	MenuItem,
	InputGroup,
	Tooltip,
} from '@blueprintjs/core';
import styled from '@emotion/styled';
import { isEmpty, isNil } from 'lodash';
import { observer } from 'mobx-react-lite';
import React, { useState, useEffect, useRef, useMemo } from 'react';

const Image = styled('img')`
	height: 20px;

	.bp5-dark & {
		filter: invert(1);
	}
`;

const CustomMenuDivider = styled('div')`
	border-top: 1px solid rgba(17, 20, 24, 0.15);
	border-color: rgba(255, 255, 255, 0.2);
	display: block;
`;

interface GlobalFont {
	fontFamily: string;
	url?: string;
}

interface CustomFont {
	url: string;
	style: string;
	weight: string;
	cssWeight?: string;
}

interface ItemPosition {
	top: number;
	height: number;
	bottom: number;
}

interface FontItemProps {
	index: number;
	fontFamily: string;
	fontWeight?: string;
	handleClick: () => void;
	modifiers: {
		active: boolean;
		disabled?: boolean;
	};
	store: any;
	isCustom: GlobalFont | undefined;
	getGoogleFontImage: (fontFamily: string) => string;
}

interface SearchInputProps {
	onChange: (value: string) => void;
	defaultValue: string;
}

interface FontMenuProps {
	store: any;
	fonts: string[];
	activeFont: string;
	activeFontLabel: string;
	onFontSelect: (fontFamily: string) => void;
	getGoogleFontImage: (fontFamily: string) => string;
	globalFonts: GlobalFont[];
	customFontFamilyGroup: string | null;
}

interface TextFontFamilyProps {
	store: any;
	elements: any[];
	useSWR: any;
	getFontsList: () => string[];
	isGoogleFontChanged: () => boolean;
	globalFonts: GlobalFont[];
	getGoogleFontsListAPI: () => string;
	getGoogleFontImage: (fontFamily: string) => string;
	fetcher: (url: string) => Promise<string[]>;
	userState: any;
	hideCompanyFonts?: boolean;
}

// Height map for different item types
const ITEM_HEIGHTS = {
	font: 35,           // Regular font item
	divider: 45,        // Divider with padding
	customerHeader: 45, // _customerFontsHeader
	systemHeader: 45,   // _systemFontsHeader
};

const getItemHeight = (fontFamily: string, index: number): number => {
	if (fontFamily.includes('_divider') && index !== 0) return ITEM_HEIGHTS.divider;
	if (fontFamily === '_customerFontsHeader') return ITEM_HEIGHTS.customerHeader;
	if (fontFamily === '_systemFontsHeader') return ITEM_HEIGHTS.systemHeader;
	return ITEM_HEIGHTS.font;
};

const FontItem: React.FC<FontItemProps> = ({
	index,
	fontFamily,
	fontWeight,
	handleClick,
	modifiers,
	store,
	isCustom,
	getGoogleFontImage,
}) => {
	useEffect(() => {
		if (isCustom) {
			store.loadFont(fontFamily);
		}
	}, [fontFamily, isCustom, store]);

	const checkFontFamilyLength = (fontFamily: string): boolean => {
		const tempElement = document.createElement('span');
		tempElement.style.fontSize = '14px';
		tempElement.style.fontFamily = fontFamily;
		tempElement.style.visibility = 'hidden';
		tempElement.textContent = fontFamily;
		document.body.appendChild(tempElement);
		const fontWidth = tempElement.offsetWidth;
		document.body.removeChild(tempElement);
		return fontWidth < 160;
	};

	const handleError = (event: React.SyntheticEvent<HTMLImageElement>): void => {
		console.log('Error loading font image', event);
	};

	if (fontFamily.includes('_divider') && index !== 0) {
		return (
			<div style={{ paddingTop: '10px' }}>
				<MenuDivider />
			</div>
		);
	}

	if (fontFamily === '_customerFontsHeader') {
		return (
			<div>
				<CustomMenuDivider />
				<div style={{ padding: '5px 0 5px 12px' }}>Company Fonts</div>
			</div>
		);
	}

	/* if (fontFamily === '_usedFontsHeader') {
		return (
			<div>
				<CustomMenuDivider />
				<div style={{ padding: '5px 0 5px 12px' }}>Used Fonts</div>
			</div>
		);
	} */

	if (fontFamily === '_systemFontsHeader') {
		return (
			<div>
				<CustomMenuDivider />
				<div style={{ padding: '5px 0 5px 12px' }}>System Fonts</div>
			</div>
		);
	}

	const inner = !isCustom ? (
		<Image
			alt={fontFamily}
			crossOrigin="anonymous"
			loading="lazy"
			src={getGoogleFontImage(fontFamily)}
			onError={handleError}
		/>
	) : (
		<Tooltip
			fill
			content={fontFamily}
			disabled={checkFontFamilyLength(fontFamily)}
			placement="right"
		>
			<div
				className="bp5-text-overflow-ellipsis bp5-fill"
				style={{ marginLeft: '5px', fontFamily, fontWeight }}
			>
				{fontFamily}
			</div>
		</Tooltip>
	);

	return (
		<MenuItem
			active={modifiers.active}
			disabled={modifiers.disabled}
			style={{
				fontFamily: `"${fontFamily}"`,
				fontWeight: `"${fontWeight}"`,
				paddingLeft: '14px',
			}}
			text={inner}
			onClick={handleClick}
		/>
	);
};

const SearchInput: React.FC<SearchInputProps> = ({ onChange, defaultValue }) => {
	const ref = useRef<HTMLInputElement>(null);
	useEffect(() => {
		if (ref.current) {
			ref.current.focus();
		}
	}, []);
	return (
		<InputGroup
			defaultValue={defaultValue}
			inputRef={ref}
			leftIcon="search"
			onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
		/>
	);
};

const FontMenu: React.FC<FontMenuProps> = ({
	store,
	fonts,
	activeFont,
	activeFontLabel,
	onFontSelect,
	getGoogleFontImage,
	globalFonts,
	customFontFamilyGroup,
}) => {
	const [query, setQuery] = useState('');
	const [scrollTop, setScrollTop] = useState(0);
	const containerRef = useRef<HTMLDivElement>(null);

	const filteredFonts = fonts.filter((font) => {
		return font.toLowerCase().indexOf(query.toLowerCase()) >= 0;
	});

	// Calculate cumulative heights for positioning
	const itemPositions = useMemo(() => {
		let currentTop = 0;
		return filteredFonts.map((font, index) => {
			const height = getItemHeight(font, index);
			const top = currentTop;
			currentTop += height;
			return { top, height, bottom: currentTop };
		});
	}, [filteredFonts]);

	const totalHeight = itemPositions[itemPositions.length - 1]?.bottom || 0;

	// Calculate visible range
	const { startIndex, endIndex } = useMemo(() => {
		const containerHeight = 400; // Fixed menu height
		const start = itemPositions.findIndex(pos => pos.bottom > scrollTop);
		const end = itemPositions.findIndex(pos => pos.top > scrollTop + containerHeight);

		return {
			startIndex: Math.max(0, start - 2), // Render 2 extra items above
			endIndex: end === -1 ? filteredFonts.length : Math.min(filteredFonts.length, end + 2)
		};
	}, [scrollTop, itemPositions, filteredFonts.length]);

	const visibleFonts = filteredFonts.slice(startIndex, endIndex);

	return (
		<Popover
			content={
				<div>
					<SearchInput defaultValue={query} onChange={(val) => setQuery(val)} />
					<div
						ref={containerRef}
						style={{
							height: '400px',
							width: '210px',
							overflowY: 'auto',
							position: 'relative'
						}}
						onScroll={(e: React.UIEvent<HTMLDivElement>) => setScrollTop(e.currentTarget.scrollTop)}
					>
						<Menu style={{ height: totalHeight, position: 'relative' }}>
							{visibleFonts.map((item, index) => {
								const actualIndex = startIndex + index;
								const position = itemPositions[actualIndex];

								return (
									<div
										key={item + actualIndex}
										style={{
											position: 'absolute',
											top: position.top,
											left: 0,
											right: 0,
											height: position.height
										}}
									>
										<FontItem
											fontFamily={item}
											fontWeight={filteredFonts[actualIndex]}
											getGoogleFontImage={getGoogleFontImage}
											handleClick={() => onFontSelect(item)}
											index={actualIndex}
											isCustom={
												store.fonts.find((f: GlobalFont) => f.fontFamily === item) ||
												globalFonts.find((f) => f.fontFamily === item)
											}
											modifiers={{
												active: activeFont === item || customFontFamilyGroup === item,
											}}
											store={store}
										/>
									</div>
								);
							})}
						</Menu>
					</div>
				</div>
			}
			fill={true}
			placement="bottom"
		>
			<Button
				minimal
				rightIcon="caret-down"
				style={{
					marginRight: '5px',
					fontFamily: `"${activeFont}"`,
					overflow: 'hidden',
					whiteSpace: 'nowrap',
					maxHeight: '30px',
				}}
				text={activeFontLabel}
			/>
		</Popover>
	);
};

export const TextFontFamily: React.FC<TextFontFamilyProps> = observer(
	({
		store,
		elements,
		useSWR,
		getFontsList,
		isGoogleFontChanged,
		globalFonts,
		getGoogleFontsListAPI,
		getGoogleFontImage,
		fetcher,
		userState,
		hideCompanyFonts = false,
	}) => {
		const [customFontFamilyGroup, setCustomFontFamilyGroup] = useState<string | null>(null);

		const googleFonts = getFontsList();

		const customFonts: Record<string, CustomFont[]> = userState?.customer?.brand?.customFonts ?? {};

		const { data, mutate } = useSWR(getGoogleFontsListAPI(), fetcher, {
			isPaused: () => isGoogleFontChanged(),
			fallbackData: [],
		});

		const usedFonts: string[] = [];
		const fontFamily = elements[0].fontFamily;

		const allFonts = globalFonts
			.map((f) => f.fontFamily)
			.concat(data?.length && !isGoogleFontChanged() ? data : googleFonts);

		useEffect(() => {
			mutate();
		}, [isGoogleFontChanged(), mutate]);

		useEffect(() => {
			if (!hideCompanyFonts) {
				if (
					userState?.customer?.brand?.customFonts &&
					Object.keys(userState?.customer?.brand?.customFonts)?.length > 0
				) {
					for (const [fontKey] of Object.entries(
						userState?.customer?.brand?.customFonts,
					)) {
						if (fontKey === elements[0]?.fontFamily) {
							setCustomFontFamilyGroup(fontKey);
						}
					}
				}
			}
		}, [elements, hideCompanyFonts, userState?.customer?.brand?.customFonts]);

		store.find((el: any) => {
			if (el.type === 'text') {
				usedFonts.push(el.fontFamily);
			}
			return false;
		});

		/**
		 * we use multiple because this data structure implies that only unique data is stored.
		 * It's kind of like a filter. Also, if a user has custom fonts, we show them in the font selection menu header.
		 * Show custom fonts first, then system fonts.
		 * The _customerFontsHeader and _systemFontsHeader keys are needed to show the headers.
		 */
		//NOTE: the use of 'Set' is to remove duplicates and maintain the order of the fonts
		//TODO: Handle when the 'usedFonts' has missing custom fonts
		const displayFonts =
			!hideCompanyFonts &&
			Object.keys(userState?.customer?.brand?.customFonts)?.length > 0
				? [
						...new Set(
							[...usedFonts]
								.concat('_customerFontsHeader')
								.concat(Object.keys(customFonts))
								.concat('_systemFontsHeader')
								.concat(allFonts),
						),
					]
				: [
						...new Set(
							[...usedFonts].concat('_systemFontsHeader').concat(allFonts),
						),
					];

		return (
			<FontMenu
				activeFont={fontFamily}
				activeFontLabel={
					customFontFamilyGroup ? customFontFamilyGroup : fontFamily
				}
				customFontFamilyGroup={customFontFamilyGroup}
				fonts={displayFonts}
				getGoogleFontImage={getGoogleFontImage}
				globalFonts={globalFonts}
				store={store}
				onFontSelect={(fontFamily: string) => {
					store.history.transaction(() => {
						elements.forEach((element) => {
							if (Object.keys(customFonts).includes(fontFamily)) {
								!hideCompanyFonts && setCustomFontFamilyGroup(fontFamily);
								for (const [fontKey] of Object.entries(customFonts)) {
									if (fontFamily === fontKey) {
										element.set({
											fontFamily: fontKey,
										});
									}
								}
							} else {
								!hideCompanyFonts && setCustomFontFamilyGroup(null);
								element.set({
									fontFamily,
								});
							}
						});
					});

					// Manage fonts associated with the template
					store.pages.forEach((page: any) => {
						page?.children.forEach((element: any) => {
							if (element.type === 'text') {
								const fontName = fontFamily;

								if (
									Object.keys(customFonts)?.length > 0 &&
									!isEmpty(customFonts[fontName]) &&
									!isNil(customFonts[fontName])
								) {
									if (customFonts[fontName]?.length === 1) {
										customFonts[fontName]?.forEach((font) => {
											store.addFont({
												fontFamily: fontName,
												url: `${font?.url}?buster=${Date.now()}&src=${encodeURIComponent(window.location.origin)}`,
											});
										});
									} else {
										const stylesArray = customFonts[fontName]?.map((font) => ({
											src: `url('${font?.url}?buster=${Date.now()}&src=${encodeURIComponent(window.location.origin)}')`,
											fontStyle: font?.style?.toLowerCase(),
											fontWeight: font?.weight?.toLowerCase(),
										}));

										store.addFont({
											fontFamily: fontName,
											styles: stylesArray,
										});
									}
								} else {
									console.log(
										'No custom fonts for this brand or used custom font is missing: ',
										fontName,
									);
									//templateStore.removeFont(fontName);
								}
							}
						});
					});
				}}
			/>
		);
	},
);

export default TextFontFamily;
